import { z } from 'zod';
import { MAX_CUSTOM_SANDBOX_DISK_GB, MIN_CUSTOM_SANDBOX_DISK_GB } from '@runloop/reflex-contract';

/**
 * Deployment-wide Reflex settings controlled by platform admins.
 *
 * These settings live in the host config store rather than an organization,
 * so one value applies to every tenant on the deployment.
 */
export const ServiceSettingsSchema = z
  .object({
    /**
     * Largest disk override an organization may save for a custom sandbox
     * size. The lower bound matches Runloop's custom-disk platform floor.
     */
    maxSandboxDiskSizeGb: z.number().int().min(MIN_CUSTOM_SANDBOX_DISK_GB),
  })
  .strict()
  .meta({ id: 'ServiceSettings' });
export type ServiceSettings = z.infer<typeof ServiceSettingsSchema>;

/**
 * Product defaults used when no platform admin has saved service settings.
 * A deployment's legacy disk-limit environment variable may supply a
 * different initial value until the setting is first saved.
 */
export const DEFAULT_SERVICE_SETTINGS: ServiceSettings = {
  maxSandboxDiskSizeGb: MAX_CUSTOM_SANDBOX_DISK_GB,
};

/** Complete replacement payload for the service settings admin endpoint. */
export const UpdateServiceSettingsRequestSchema = ServiceSettingsSchema.meta({
  id: 'UpdateServiceSettingsRequest',
});
export type UpdateServiceSettingsRequest = z.infer<typeof UpdateServiceSettingsRequestSchema>;
